import React, { useState, useEffect } from "react";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, XCircle, Download, Clock } from "lucide-react";

export default function ConversionsPage() {
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");

  const fetchConversions = async () => {
    setLoading(true);
    try {
      const { data } = await apiCall("get", `/conversions?status=${statusFilter}`);
      setConversions(data.conversions);
    } catch { toast.error("Failed to load conversions"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchConversions(); }, [statusFilter]);

  const handleVerify = async (clientUserId, action) => {
    try {
      await apiCall("post", "/conversions/verify", { client_user_id: clientUserId, action });
      toast.success(`Conversion ${action}d`);
      fetchConversions();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/conversions-download`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "conversions_report.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Download failed"); }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
      approved: "bg-green-500/10 text-green-600 border-green-500/20",
      rejected: "bg-red-500/10 text-red-600 border-red-500/20"
    };
    return <Badge className={`text-xs capitalize ${map[status] || ""}`}>{status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="conversions-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Client Conversions</h2>
          <p className="text-sm text-muted-foreground mt-1">Review and approve agent-submitted conversions</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="conversion-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={downloadReport} data-testid="download-conversions-btn">
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Pending</p>
              <p className="text-xl font-bold">{conversions.filter(c => c.status === "pending").length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Approved</p>
              <p className="text-xl font-bold">{conversions.filter(c => c.status === "approved").length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <XCircle className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Rejected</p>
              <p className="text-xl font-bold">{conversions.filter(c => c.status === "rejected").length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider">Client ID</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Agent</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Deposit Amount</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Deposit Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Calling Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" /></TableCell></TableRow>
              ) : conversions.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No conversions found</TableCell></TableRow>
              ) : (
                conversions.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium text-sm"><button className="text-primary hover:underline" onClick={() => {navigator.clipboard.writeText(`${c.client_user_id}`); toast.success("Copied");}}>{c.client_user_id}</button></TableCell>
                    <TableCell className="text-sm">{c.agent_name}</TableCell>
                    <TableCell className="text-sm font-semibold">{c.deposit_amount}</TableCell>
                    <TableCell className="text-sm">{c.deposit_date}</TableCell>
                    <TableCell className="text-sm">{c.calling_date ? new Date(c.calling_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : "-"}</TableCell>
                    <TableCell>{getStatusBadge(c.status)}</TableCell>
                    <TableCell>
                      {c.status === "pending" ? (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="text-green-600 hover:text-green-700" onClick={() => handleVerify(c.client_user_id, "approve")} data-testid={`approve-${c.client_user_id}`}>
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleVerify(c.client_user_id, "reject")} data-testid={`reject-${c.client_user_id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">{c.verified_at ? new Date(c.verified_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : "-"}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
